import { useState, useEffect, useRef, useMemo } from 'react'
import { X, Search, CheckCircle2, Clock, Archive } from 'lucide-react'
import type { EventSummary } from '@/contexts/AuthContext'
import { deriveStatus, formatDateRange, shortenName, type EventStatus } from '@/lib/eventUtils'

interface EventSelectorModalProps {
  open: boolean
  onClose: () => void
  onSelectEvent: (eventId: number) => void
  events: EventSummary[]
  currentEventId: number | null
}

const STATUS_CONFIG = {
  active: { label: 'Current', icon: CheckCircle2, dot: 'bg-green-500' },
  upcoming: { label: 'Upcoming', icon: Clock, dot: 'bg-amber-400' },
  completed: { label: 'Past', icon: Archive, dot: 'bg-slate-400' },
} as const

export function EventSelectorModal({
  open,
  onClose,
  onSelectEvent,
  events,
  currentEventId,
}: EventSelectorModalProps) {
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSearch('')
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const enriched = useMemo(() =>
    events.map((ev) => ({
      ...ev,
      status: deriveStatus(ev.StartDate, ev.EndDate),
      shortName: shortenName(ev.EventName),
      dateRange: formatDateRange(ev.StartDate, ev.EndDate),
    })),
    [events]
  )

  const filtered = useMemo(() =>
    enriched.filter((e) =>
      e.EventName.toLowerCase().includes(search.toLowerCase())
    ),
    [enriched, search]
  )

  const grouped = useMemo(() => {
    const g: Record<EventStatus, typeof filtered> = { active: [], upcoming: [], completed: [] }
    for (const e of filtered) g[e.status].push(e)
    return g
  }, [filtered])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Select event"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl border border-slate-200 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="font-display font-bold text-lg text-ink-dark">Select Event</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-soft hover:bg-slate-100 hover:text-ink-dark"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-ink placeholder:text-ink-hint focus:outline-none focus:ring-2 focus:ring-butler-600/20 focus:border-butler-600"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {(Object.keys(grouped) as EventStatus[]).map((status) => {
            const items = grouped[status]
            if (items.length === 0) return null
            const config = STATUS_CONFIG[status]
            const Icon = config.icon
            return (
              <div key={status} className="mb-4">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <Icon className="h-3.5 w-3.5 text-ink-soft" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
                    {config.label}
                  </span>
                </div>
                <ul className="space-y-0.5">
                  {items.map((event) => (
                    <li key={event.EventID}>
                      <button
                        type="button"
                        onClick={() => {
                          onSelectEvent(event.EventID)
                          onClose()
                        }}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                          event.EventID === currentEventId
                            ? 'bg-butler-50 border border-butler-200'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${config.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink-dark truncate">
                            {event.shortName}
                          </div>
                          {event.dateRange && (
                            <div className="text-xs text-ink-soft mt-0.5">{event.dateRange}</div>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-ink-soft">
              No events match &ldquo;{search}&rdquo;
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
